import { useParams, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Issue from "./Issue";
import Articleid from "./Articleid/Articleid";

const DynamicRoute = () => {
  const { article } = useParams();
  const location = useLocation();

  // Prefer data passed via state when navigating from listings
  const articleState = location.state?.article || location.state || {};
  const abstract = articleState.abstract || "";
  const keywords = Array.isArray(articleState.keywords)
    ? articleState.keywords.join(", ")
    : articleState.keywords || "";
  const authors = articleState.authors || articleState.author || "";
  const image = articleState.image || "";
  const publishedDate = articleState.publishedDate || articleState.date || "";

  // Determine the title dynamically
  const getTitle = () => {
    // Use explicit title if available, else humanize slug
    const explicitTitle = articleState.title;
    if (explicitTitle && explicitTitle.trim().length > 0) {
      return `${explicitTitle} | Journal of Applied Bioanalysis`;
    }
    if (article) {
      return `${article
        .split("-")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")} | Journal of Applied Bioanalysis`;
    }
    return "Journal of Applied Bioanalysis";
  };

  // Build a stable canonical URL with a sanitized slug (remove punctuation like ':')
  const canonicalUrl = (() => {
    if (typeof window === "undefined") return "";
    const { origin } = window.location;
    const rawSlug = article || "";
    const cleanSlug = rawSlug
      .toLowerCase()
  .replace(/[\u2013\u2014]/g, "-") // replace en/em dash first
  .replace(/[\/:()]/g, "-")        // remove slash, colon, brackets
  .replace(/[.]/g, "")             // remove dots
  .replace(/[^a-z0-9-]+/g, "-")    // replace remaining invalid chars
  .replace(/--+/g, "-")            // collapse multiple '-'
  .replace(/^-|-$/g, "");          // trim leading/trailing '-'
    console.log("clean slug", cleanSlug);

    return `${origin}/${cleanSlug || rawSlug}`;
  })();

  // JSON-LD structured data for articles
  const articleJsonLd = article
    ? {
        "@context": "https://schema.org",
        "@type": "ScholarlyArticle",
        headline: articleState.title || article.replace(/-/g, " "),
        description: abstract,
        author: authors ? [{ "@type": "Person", name: authors }] : undefined,
        datePublished: publishedDate || undefined,
        image: image || undefined,
        inLanguage: "en",
        mainEntityOfPage: canonicalUrl,
        isPartOf: {
          "@type": "Periodical",
          name: "Journal of Applied Bioanalysis",
        },
        url: canonicalUrl,
        keywords: keywords || undefined,
      }
    : null;

  return (
    <>
      <Helmet>
        <title>{getTitle()}</title>
        {/* Standard SEO */}
        {abstract && <meta name="description" content={abstract} />}
        {keywords && <meta name="keywords" content={keywords} />}
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={getTitle()} />
        {abstract && <meta property="og:description" content={abstract} />}
        <meta property="og:url" content={canonicalUrl} />
        {image && <meta property="og:image" content={image} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getTitle()} />
        {abstract && <meta name="twitter:description" content={abstract} />}
        {image && <meta name="twitter:image" content={image} />}

        {/* Structured data */}
        {articleJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(articleJsonLd)}
          </script>
        )}
      </Helmet>
      {location.pathname.startsWith("/journal-of-applied-bioanalysis") ? (
        <Issue />
      ) : (
        <Articleid />
      )}
    </>
  );
};

export default DynamicRoute;
